import dayjs from "dayjs"
import { Button, Layout, Modal, Typography } from "matsuri-ui"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { css } from "@emotion/react"
import { useCallback, useContext, useMemo, useState } from "react"
import { CleaningDetailProvider } from "./providers/cleaningDetail"
import { CleaningInfoRoutes } from "./routes"
import {
    CleaningPhotoTourProvider,
    useCleaningPhotoTourContext
} from "./providers/photoTour"
import { useCleaningDetail } from "./providers/cleaningDetail"
import { ToTroubleReportButton } from "./ToTroubleReportButton"
import {
    KeyboxRotationContext,
    KeyboxRotationProvider
} from "./providers/keyboxrotation"
import { MobileAppHeaderProps } from "@/components/Header/MobileAppHeader"
import { CleaningStatus } from "@/model/cleaningInfo"
import { Footer } from "@/components/Footer/Footer"
import { isTaskDateReached } from "@/utils/isTaskDateReached"
import { useOperationIsReadyToStart } from "@/hooks/useIsReadyToStartCleaning"
import { Header } from "@/components/Header/Header"
import { useTranslation } from "@/hooks/useTranslation"
import { useLostItemReport } from "@/hooks/useLostItemReport"
import { genCleaningInfoPagePath } from "@/routes/protected"

const ToTroubleReportButtonWithLink = ({
    detailId,
    pathname
}: {
    detailId: string | undefined
    pathname: string
}) => {
    const URLBlackList = ["list-troubles", "trouble", "lost-items"]

    const buttonVisible = URLBlackList.reduce((prev, curr) => {
        return prev && !pathname.includes(curr)
    }, true)

    // 忘れ物報告は対象エリアの清掃のみ。対象と確定するまで（取得中・失敗・対象外）は
    // 従来通りトラブルだけの吹き出しにする。
    const { isTarget } = useLostItemReport(detailId ?? "")

    if (!buttonVisible) return null

    return (
        <ToTroubleReportButton
            toTroubleReportLink={`/cleanings/${detailId}/trouble`}
            toLostItemReportLink={`/cleanings/${detailId}/lost-items`}
            lostItemEnabled={isTarget === true}
        />
    )
}

type HeaderProps = MobileAppHeaderProps

const CleaningInfoHeader = ({ title }: HeaderProps) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { cleaningId } = useParams<"cleaningId">()

    return (
        <Header
            css={css`
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 100%;
                //7px+Buttonのpadding(12px)
                padding: 19px 50px;
                padding-top: calc(19px + env(safe-area-inset-top, 0px));
            `}
        >
            <Button
                onClick={() => {
                    // 画面遷移がうまくいかないため、条件分岐を追加
                    if (location.pathname.endsWith("/info")) {
                        navigate("/")
                    } else if (location.pathname.endsWith("/errand")) {
                        // お願い事一覧から戻る場合は清掃詳細へ直接遷移
                        // （撮影完了後に一覧に戻ると履歴が2つ積まれるため）
                        navigate(genCleaningInfoPagePath(cleaningId!, "info"))
                    } else {
                        navigate(-1)
                    }
                }}
                css={css`
                    position: absolute;
                    left: 0;
                    padding: 12px;
                `}
            >
                <ArrowBackIcon />
            </Button>
            <Typography
                variant="h4"
                clamp={1}
                css={css`
                    text-align: center;
                `}
            >
                {title}
            </Typography>
        </Header>
    )
}

const footerViewURLBlackList = ["take-photo", "manual/"]
interface InfoFooterProps {
    status: CleaningStatus
    requestStart: () => void
    requestFinish: () => void
    requestGoToReport: () => void
    isAllKeyboxesRotated?: boolean
}

const InfoFooter = ({
    requestStart,
    requestFinish,
    requestGoToReport,
    status,
    isAllKeyboxesRotated
}: InfoFooterProps) => {
    const { t } = useTranslation()
    const [finishDialogOpen, setFinishDialogOpen] = useState(false)
    const [aborted, setAborted] = useState(false)
    const location = useLocation()
    const visibleFooter = useMemo(
        () =>
            footerViewURLBlackList.reduce((prev, curr) => {
                return prev && !location.pathname.includes(curr)
            }, true),
        [location.pathname]
    )

    const openConfirmFinishDialog = useCallback(() => {
        setFinishDialogOpen(true)
    }, [])

    const closeConfirmFinishDialog = useCallback(() => {
        setFinishDialogOpen(false)
    }, [])

    const confirmFinish = useCallback(() => {
        requestFinish()
    }, [requestFinish])

    const handleAbort = useCallback(() => {
        setAborted(true)
    }, [])

    const handleStart = useCallback(() => {
        requestStart()
        setAborted(false)
    }, [requestStart])

    const { detail } = useCleaningDetail()
    const { data: operationIsReadyToStart } = useOperationIsReadyToStart({
        operationId: detail?.id
    })

    // 清掃予定日より前の開始打刻は、報告日ベースの送金集計で誤りになるため防ぐ
    const taskDateReached = isTaskDateReached(detail?.cleaningInfo.cleaningDate)

    const isStartable = !!(
        isAllKeyboxesRotated &&
        operationIsReadyToStart?.isReady &&
        taskDateReached
    )

    if (visibleFooter) {
        return (
            <>
                {status === "prepared" || aborted ? (
                    <Footer>
                        <Layout.Column gap={2}>
                            <ToTroubleReportButtonWithLink
                                detailId={detail?.id}
                                pathname={location.pathname}
                            />

                            {!taskDateReached ? (
                                <div>
                                    <Typography
                                        variant="h4"
                                        as="h4"
                                        align="center"
                                    >
                                        {t(
                                            "清掃予定日は{{cleaningDate}}です。本日開始するには管理者による予定日の変更が必要です。",
                                            {
                                                params: {
                                                    cleaningDate: dayjs(
                                                        detail?.cleaningInfo
                                                            .cleaningDate
                                                    ).format("M/D")
                                                }
                                            }
                                        )}
                                    </Typography>
                                </div>
                            ) : !operationIsReadyToStart?.isReady ? (
                                <div>
                                    <Typography
                                        variant="h4"
                                        as="h4"
                                        align="center"
                                    >
                                        {t("承認を完了してください！")}
                                        <Link
                                            css={css`
                                                margin-left: 8px;
                                                color: #4a4a4a;
                                                text-decoration: underline;
                                            `}
                                            to="/approval_requests"
                                        >
                                            {t("承認一覧へ")}
                                        </Link>
                                    </Typography>
                                </div>
                            ) : !isAllKeyboxesRotated ? (
                                <div>
                                    <Typography
                                        variant="h4"
                                        as="h4"
                                        align="center"
                                    >
                                        {t(
                                            "キーボックスの番号を変更してください"
                                        )}
                                    </Typography>
                                </div>
                            ) : null}
                            <div>
                                <div
                                    css={css`
                                        /* Buttonのdisabled時にボタン色はopactiyで制御しているため、 */
                                        /* Overlay上にopacityをかけるのではなく白背景にopacityをかける */
                                        background-color: white;
                                        border-radius: 4px;
                                    `}
                                >
                                    <Button
                                        disabled={!isStartable}
                                        fullWidth
                                        variant={"filled"}
                                        color={"primary"}
                                        onClick={handleStart}
                                    >
                                        {t("開始")}
                                    </Button>
                                </div>
                            </div>
                        </Layout.Column>
                    </Footer>
                ) : status === "started" ? (
                    <Footer handleClose={handleAbort}>
                        <ToTroubleReportButtonWithLink
                            detailId={detail?.id}
                            pathname={location.pathname}
                        />
                        <Button
                            fullWidth
                            variant={"filled"}
                            color={"secondary"}
                            onClick={openConfirmFinishDialog}
                        >
                            {t("終了")}
                        </Button>
                    </Footer>
                ) : status === "finished" ? (
                    <Footer>
                        <ToTroubleReportButtonWithLink
                            detailId={detail?.id}
                            pathname={location.pathname}
                        />
                        <Button
                            fullWidth
                            variant={"filled"}
                            onClick={requestGoToReport}
                        >
                            {t("報告へ")}
                        </Button>
                    </Footer>
                ) : null}
                <Modal
                    open={finishDialogOpen}
                    backdrop
                    body={
                        <Layout.Column gap={4}>
                            <div>
                                <Typography align={"center"} variant={"h2"}>
                                    {t("清掃終了")}
                                </Typography>
                            </div>
                            <div>
                                <Typography align={"center"}>
                                    {t("清掃を終了して、報告を開始しますか？")}
                                </Typography>
                            </div>
                        </Layout.Column>
                    }
                    footer={
                        <Layout.Row gap={1} nowrap>
                            <Button
                                variant={"filled"}
                                fullWidth
                                onClick={closeConfirmFinishDialog}
                            >
                                {t("キャンセル")}
                            </Button>
                            <Button
                                variant={"filled"}
                                color={"primary"}
                                fullWidth
                                onClick={confirmFinish}
                            >
                                OK
                            </Button>
                        </Layout.Row>
                    }
                />
            </>
        )
    } else {
        return null
    }
}

const CleaningInfoApp = () => {
    const { requestStart, requestFinish, detail } = useCleaningDetail()
    const { tour } = useCleaningPhotoTourContext()
    const title = useMemo(() => detail?.cleaningInfo.listingName, [detail])

    const navigate = useNavigate()
    const { cleaningId } = useParams()

    const tourPath = useMemo(
        () => (tour ? `/tour/${cleaningId}` : `/reports/${cleaningId}`),
        [cleaningId, tour]
    )

    const handleStart = useCallback(async () => {
        requestStart()
    }, [requestStart])

    const handleFinish = useCallback(async () => {
        requestFinish()
        navigate(tourPath)
    }, [navigate, requestFinish, tourPath])

    const handleGotoReport = useCallback(async () => {
        navigate(tourPath)
    }, [navigate, tourPath])

    const { isAllRotated } = useContext(KeyboxRotationContext)

    return (
        <div
            css={css`
                padding-bottom: 250px; //footer部分に被らない高さ
            `}
        >
            <CleaningInfoHeader title={title} />
            <CleaningInfoRoutes />
            {detail ? (
                <InfoFooter
                    status={detail.status}
                    requestStart={handleStart}
                    requestFinish={handleFinish}
                    requestGoToReport={handleGotoReport}
                    isAllKeyboxesRotated={isAllRotated}
                />
            ) : null}
        </div>
    )
}

export const CleaningInfoPage = () => {
    return (
        <CleaningDetailProvider>
            <CleaningPhotoTourProvider>
                <KeyboxRotationProvider>
                    <CleaningInfoApp />
                </KeyboxRotationProvider>
            </CleaningPhotoTourProvider>
        </CleaningDetailProvider>
    )
}
